/**
 * DisqualificationChart Component
 *
 * Donut chart for disqualification reasons distribution
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

interface DisqualificationData {
  reason: string;
  count: number;
  color: string;
}

interface DisqualificationChartProps {
  data: DisqualificationData[];
  onReasonClick?: (reason: string) => void;
  activeReason?: string | null;
}

const DisqualificationChart: React.FC<DisqualificationChartProps> = ({ data, onReasonClick, activeReason }) => {
  const isClickable = !!onReasonClick;

  const total = data.reduce((sum, item) => sum + item.count, 0);
  const centerX = 150;
  const centerY = 150;
  const radius = 100;
  const holeRadius = 65;

  // Calculate paths for donut segments
  const createDonutSegment = (startAngle: number, endAngle: number) => {
    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    const x3 = centerX + holeRadius * Math.cos(endRad);
    const y3 = centerY + holeRadius * Math.sin(endRad);
    const x4 = centerX + holeRadius * Math.cos(startRad);
    const y4 = centerY + holeRadius * Math.sin(startRad);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${holeRadius} ${holeRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;
  };

  let currentAngle = 0;
  const segments = data.map((item) => {
    const percentage = total > 0 ? (item.count / total) * 100 : 0;
    const angle = (percentage / 100) * 360;
    const segment = {
      ...item,
      percentage,
      startAngle: currentAngle,
      endAngle: currentAngle + angle,
    };
    currentAngle += angle;
    return segment;
  });

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
      {/* Donut Chart */}
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <svg width="300" height="300" viewBox="0 0 300 300">
          {segments.map((segment, index) => {
            const isActive = activeReason === segment.reason;
            return (
              <path
                key={index}
                d={createDonutSegment(segment.startAngle, segment.endAngle)}
                fill={segment.color}
                stroke="white"
                strokeWidth="2"
                style={{
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: 'opacity 0.2s',
                  opacity: activeReason === null || isActive ? 1 : 0.3,
                }}
                onClick={isClickable ? () => onReasonClick(segment.reason) : undefined}
                onMouseEnter={(e) => {
                  if (isClickable && (activeReason === null || isActive)) {
                    e.currentTarget.style.opacity = '0.8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeReason === null || isActive) {
                    e.currentTarget.style.opacity = '1';
                  } else {
                    e.currentTarget.style.opacity = '0.3';
                  }
                }}
              >
                <title>{`${segment.reason}: ${segment.count} (${segment.percentage.toFixed(1)}%)`}</title>
              </path>
            );
          })}
        </svg>
        {/* Center label */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {total}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total
          </Typography>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {segments.map((segment, index) => {
          const isActive = activeReason === segment.reason;
          return (
            <Box
              key={index}
              onClick={isClickable ? () => onReasonClick(segment.reason) : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                py: 0.5,
                px: 0.75,
                borderRadius: 1,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.2s',
                bgcolor: isActive ? 'action.selected' : 'transparent',
                opacity: activeReason === null || isActive ? 1 : 0.4,
                '&:hover': isClickable ? {
                  bgcolor: isActive ? 'action.selected' : 'action.hover',
                } : {},
              }}
            >
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: segment.color,
                borderRadius: 0.5,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }} noWrap>
                {segment.reason}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', lineHeight: 1.2 }}>
                {segment.count} ({segment.percentage.toFixed(1)}%)
              </Typography>
            </Box>
          </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default DisqualificationChart;
